import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ChangeDetectionStrategy,
    forwardRef,
    Injector,
    ViewContainerRef,
    inject
} from "@angular/core";
import { ComponentPortal } from "@angular/cdk/portal";

import { useTranslationNamespace } from "@logex/framework/lg-localization";

import { IFlaggedWithDetails, MIXED_FLAGS_STATUS } from "@shared/types/types";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { FlagsMenuComponentBase } from "../flags-menu/flags-menu-base.componet";
import { SaveFlagsObject } from "./types";
import { DiffCostIndicatorsColors } from "../flags-menu/types";
import { FlagsSelectorDialog } from "./flags-selector-dialog.component";
import { InfoRecordComponent, RECORD_INFO_DATA } from "../info-record/info-record.component";

@Component({
    selector: "flags-selector",
    templateUrl: "./flags-selector.component.html",
    styleUrls: ["./flags-selector.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [useTranslationNamespace("APP._Variation.CostDiffIndicators")],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => FlagsSelectorComponent)
        }
    ],
    standalone: false
})
export class FlagsSelectorComponent
    extends FlagsMenuComponentBase<SaveFlagsObject>
    implements OnChanges
{
    private _container = inject(ViewContainerRef);
    private _dialog = inject(FlagsSelectorDialog);
    @Input() current: IFlaggedWithDetails;
    @Input() hideDetails = false;

    get value(): SaveFlagsObject | undefined {
        // if (this.current) return this._value;
        return this._value;
    }

    set value(value: SaveFlagsObject | undefined) {
        if (this._value !== value) {
            this._value = value;

            // if (this.current) {
            //    this.current.flag_id = value?.flag_id ?? MIXED_FLAGS_STATUS;
            //    this.current.flag_comment = value?.flag_comment;
            // }
        }
    }

    writeValue(value: SaveFlagsObject): void {
        // if (this.current) {
        //    this.current.flag_id = value.flag_id;
        //    this.current.flag_comment = value.flag_comment;
        // }
        super.writeValue(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes.current) {
            this._value = {
                flag_id: this.current.flag_id || 0,
                flag_comment: this.current.flag_comment
            };
        }
    }

    _showFlagsOptionsPopup(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        if (this._disabled) return;

        this._dialog
            .open(
                this.bulkUpdate
                    ? { flag_id: MIXED_FLAGS_STATUS, flag_comment: undefined }
                    : this.value ?? { flag_id: MIXED_FLAGS_STATUS, flag_comment: undefined },
                this.definition
            )
            .subscribe(async x => {
                if (this._touched) {
                    this._touched();
                }
                if (x) {
                    if (this.bulkUpdate) {
                        this.bulkUpdateFlags(x);
                    } else {
                        this.flagChanged(x);
                    }
                }
            });
    }

    protected async flagChanged(newValue: SaveFlagsObject): Promise<boolean> {
        this.raiseFlagSaving(newValue);
        return true;
    }

    protected async bulkUpdateFlags(newValue: SaveFlagsObject): Promise<void> {
        this.raiseFlagSaving(newValue);
    }

    _getColor(): number {
        if (!this.current || this.current.flag_id === MIXED_FLAGS_STATUS) {
            return DiffCostIndicatorsColors.Unmarked;
        } else {
            return (
                this.definition.find(x => this.current?.flag_id === x.code)?.type ??
                DiffCostIndicatorsColors.Unmarked
            );
        }
    }

    get _hasColor() {
        return (
            this.value !== undefined &&
            this.value.flag_id > 0 &&
            this.value.flag_id !== MIXED_FLAGS_STATUS
        );
    }

    get _isMixedState() {
        return this.value !== undefined && this.value.flag_id === MIXED_FLAGS_STATUS;
    }

    get _isMixedCommentState() {
        return this.value?.flag_id === MIXED_FLAGS_STATUS;
    }

    _currentText(): string | ComponentPortal<any> {
        if (
            !this._hasColor ||
            this._isMixedState ||
            this._isMixedCommentState ||
            this.hideDetails
        ) {
            if (this.value?.flag_id === MIXED_FLAGS_STATUS)
                return this._lgTranslate.translate(".Mixed");
            return this.definition[this.value?.flag_id ?? ""]?.name || "";
        } else {
            const portalInjector = Injector.create({
                providers: [
                    {
                        provide: RECORD_INFO_DATA,
                        useValue: {
                            name: this.definition[this.value?.flag_id ?? ""]?.name,
                            text: this.value?.flag_comment,
                            lastAuthor: this.current?.flag_user_id_inserted,
                            lastChange: this.current?.flag_date_inserted
                        }
                    }
                ],
                parent: this._container.injector
            });

            return new ComponentPortal(InfoRecordComponent, null, portalInjector);
        }
    }
}
